import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AssetUrls } from 'config/assets';
import './SmsAlertsIntroModal.css';

// Temple Bell art crop — uploaded to the assets bucket; served via the standard CDN path.
const TEMPLE_BELL_HERO = AssetUrls.custom('/assets/cards-crops/templebell.jpg');
// Same bell SVG the Have List alert bell uses, so the hero icon is a true replica.
const BELL_ICON =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/notifications_active_24dp.svg';

interface SmsAlertsIntroModalProps {
  isOpen: boolean;
  /** "Enable SMS Alerts" CTA — proceed with the original enabling action. */
  onConfirm: () => void;
  /** Dismiss / backdrop click / Escape — cancel the original enabling action. */
  onDismiss: () => void;
}

/**
 * First-time educational/consent modal for the SMS Alerts feature. Interrupts
 * the first attempt to enable SMS alerts so the user gives informed consent
 * before any text message can be sent. Visual treatment mirrors EliteUpsellPrompt.
 */
export const SmsAlertsIntroModal: React.FC<SmsAlertsIntroModalProps> = ({
  isOpen,
  onConfirm,
  onDismiss,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="sms-alerts-intro-modal-overlay"
      onClick={onDismiss}
      role="presentation"
    >
      <div
        className="sms-alerts-intro-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-alerts-intro-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sms-alerts-intro-modal__header"
          style={{ backgroundImage: `url(${TEMPLE_BELL_HERO})` }}
        >
          <div className="sms-alerts-intro-modal__header-overlay" />
          <div className="sms-alerts-intro-modal__bell" aria-hidden="true">
            <img
              src={BELL_ICON}
              alt=""
              className="sms-alerts-intro-modal__bell-icon"
            />
          </div>
        </div>

        <div className="sms-alerts-intro-modal__body">
          <h2
            id="sms-alerts-intro-modal-title"
            className="sms-alerts-intro-modal__title"
          >
            SMS Notifications
          </h2>
          <p className="sms-alerts-intro-modal__text">
            Get a text message whenever someone wants a card you have.
          </p>
          <button
            type="button"
            className="sms-alerts-intro-modal__cta"
            onClick={onConfirm}
          >
            Enable SMS Alerts
          </button>
          <button
            type="button"
            className="sms-alerts-intro-modal__dismiss"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SmsAlertsIntroModal;
